import { useCallback, useEffect, useState } from "react";
import { listEffectiveServiceOfferings, listServiceWorkQueues, upsertServiceOffering } from "@/lib/service/client";
import type { EffectiveServiceOffering, ServiceOffering, ServiceWorkQueue } from "@/lib/service/types";
import { confirmDiscardDirty, useDirtyFormGuard } from "./servicePanelUtils";
import { newServiceJobTypeQueueId } from "./serviceJobTypesConstants";
import { offeringToDraft } from "./serviceSetupOfferingDraft";
import { slugifyServiceOfferingLabel } from "@/lib/service/serviceOfferingSlug";

export type ServiceOfferingDraft = {
  id?: string;
  label: string;
  slug: string;
  description: string;
  default_queue_id: string;
  default_duration_min: string;
  base_price_dollars: string;
  is_advertised: boolean;
  quotable_from_sales: boolean;
};

export const BLANK_SERVICE_OFFERING_DRAFT: ServiceOfferingDraft = {
  label: "",
  slug: "",
  description: "",
  default_queue_id: "",
  default_duration_min: "",
  base_price_dollars: "",
  is_advertised: true,
  quotable_from_sales: false,
};

export function useServiceOfferingsCatalog(navTick: number) {
  const [offerings, setOfferings] = useState<ServiceOffering[]>([]);
  const [inheritedOfferings, setInheritedOfferings] = useState<EffectiveServiceOffering[]>([]);
  const [queues, setQueues] = useState<ServiceWorkQueue[]>([]);
  const [queuesUnavailable, setQueuesUnavailable] = useState(false);
  const [listBusy, setListBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [draft, setDraft] = useState<ServiceOfferingDraft>(BLANK_SERVICE_OFFERING_DRAFT);
  const [draftBaseline, setDraftBaseline] = useState(JSON.stringify(BLANK_SERVICE_OFFERING_DRAFT));
  const [showForm, setShowForm] = useState(false);
  const [selectedOfferingId, setSelectedOfferingId] = useState<string | null>(null);

  const formDirty = showForm && draftBaseline !== JSON.stringify(draft);
  useDirtyFormGuard(formDirty);

  function applyDraft(next: ServiceOfferingDraft) {
    setDraft(next);
    setDraftBaseline(JSON.stringify(next));
  }

  const reload = useCallback(async () => {
    setListBusy(true);
    setLocalError(null);
    const [offRes, queueRes] = await Promise.all([
      listEffectiveServiceOfferings({ include_inactive: true }),
      listServiceWorkQueues({ active_only: true }),
    ]);
    setListBusy(false);
    if (!offRes.ok) {
      setLocalError(offRes.error);
      return;
    }
    const effective = offRes.data.offerings;
    setOfferings(effective.filter((o) => !o.is_inherited));
    setInheritedOfferings(effective.filter((o) => o.is_inherited));
    if (queueRes.ok) {
      setQueues(queueRes.data.queues);
      setQueuesUnavailable(false);
    } else {
      setQueues([]);
      setQueuesUnavailable(true);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload, navTick]);

  useEffect(() => {
    if (!showForm || draft.id || draft.default_queue_id) return;
    const defaultId = newServiceJobTypeQueueId(queues);
    if (defaultId) setDraft((d) => ({ ...d, default_queue_id: defaultId }));
  }, [draft.default_queue_id, draft.id, queues, showForm]);

  useEffect(() => {
    if (!showForm || draft.id) return;
    setDraft((d) => ({ ...d, slug: slugifyServiceOfferingLabel(d.label) }));
  }, [draft.id, draft.label, showForm]);

  const resetDraft = useCallback(() => {
    applyDraft(BLANK_SERVICE_OFFERING_DRAFT);
    setShowForm(false);
    setSelectedOfferingId(null);
  }, []);

  const tryResetDraft = useCallback(() => {
    if (!confirmDiscardDirty(formDirty)) return false;
    resetDraft();
    return true;
  }, [formDirty, resetDraft]);

  const openCreateForm = useCallback(() => {
    if (showForm && !confirmDiscardDirty(formDirty)) return;
    const defaultQueueId = newServiceJobTypeQueueId(queues);
    applyDraft({
      ...BLANK_SERVICE_OFFERING_DRAFT,
      default_queue_id: defaultQueueId,
    });
    setSelectedOfferingId(null);
    setShowForm(true);
  }, [formDirty, queues, showForm]);

  const openEditForm = useCallback((off: ServiceOffering) => {
    if (selectedOfferingId === off.id && showForm) {
      tryResetDraft();
      return;
    }
    if (!confirmDiscardDirty(formDirty)) return;
    applyDraft(offeringToDraft(off));
    setSelectedOfferingId(off.id);
    setShowForm(true);
  }, [formDirty, showForm, selectedOfferingId, tryResetDraft]);

  const loadOfferingDraft = useCallback((off: ServiceOffering) => {
    applyDraft(offeringToDraft(off));
  }, []);

  const saveOffering = useCallback(async (): Promise<ServiceOffering | null> => {
    const label = draft.label.trim();
    if (!label) {
      setLocalError("Service name is required.");
      return null;
    }
    const slug = draft.id ? draft.slug.trim() : slugifyServiceOfferingLabel(label);
    if (!slug) {
      setLocalError("Could not derive a service tag from the name.");
      return null;
    }
    setActionBusy(true);
    setLocalError(null);
    const priceRaw = draft.base_price_dollars.trim();
    const priceDollars = priceRaw ? Number(priceRaw) : null;
    const durationRaw = draft.default_duration_min.trim();
    const duration = durationRaw ? Number(durationRaw) : null;
    const res = await upsertServiceOffering({
      id: draft.id,
      label,
      slug,
      description: draft.description.trim(),
      default_queue_id: draft.default_queue_id || null,
      default_duration_min: duration != null && Number.isFinite(duration) ? duration : null,
      base_price_cents:
        priceDollars != null && Number.isFinite(priceDollars)
          ? Math.round(priceDollars * 100)
          : null,
      is_active: draft.id
        ? offerings.find((o) => o.id === draft.id)?.is_active ?? true
        : true,
      is_advertised: draft.is_advertised,
      quotable_from_sales: draft.quotable_from_sales,
    });
    setActionBusy(false);
    if (!res.ok) {
      setLocalError(res.error);
      return null;
    }
    if (res.data.product_types_synced) {
      window.dispatchEvent(new CustomEvent("org-settings-saved"));
    }
    const saved = res.data.offering;
    resetDraft();
    await reload();
    return saved;
  }, [draft, offerings, reload, resetDraft]);

  const patchOffering = useCallback(
    async (offering: ServiceOffering, patch: Partial<ServiceOffering>) => {
      setActionBusy(true);
      const res = await upsertServiceOffering({
        id: offering.id,
        label: offering.label,
        slug: offering.slug,
        description: offering.description,
        default_queue_id: offering.default_queue_id,
        default_rig_id: offering.default_rig_id,
        default_duration_min: offering.default_duration_min,
        base_price_cents: offering.base_price_cents,
        is_advertised: offering.is_advertised,
        quotable_from_sales: offering.quotable_from_sales,
        is_active: offering.is_active,
        ...patch,
      });
      setActionBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return;
      }
      if (res.data.product_types_synced) {
        window.dispatchEvent(new CustomEvent("org-settings-saved"));
      }
      await reload();
    },
    [reload]
  );

  const toggleActive = useCallback(
    async (offering: ServiceOffering) => {
      await patchOffering(offering, { is_active: !offering.is_active });
    },
    [patchOffering]
  );

  return {
    offerings,
    inheritedOfferings,
    queues,
    queuesUnavailable,
    listBusy,
    actionBusy,
    localError,
    draft,
    setDraft,
    showForm,
    selectedOfferingId,
    openCreateForm,
    openEditForm,
    loadOfferingDraft,
    saveOffering,
    patchOffering,
    toggleActive,
    reload,
    resetDraft,
    cancelForm: tryResetDraft,
  };
}
